import { useAuth } from "@/contexts/AuthContext";
import { NewWorkerMessageDialog } from "@/features/workers/components/NewWorkerMessageDialog";
import { WorkerConversationList } from "@/features/workers/components/WorkerConversationList";
import { useUnifiedWorkerCommunications } from "@/features/workers/hooks/useUnifiedWorkerCommunications";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { MessageCircle } from "lucide-react";

export const Route = createFileRoute("/dashboard/admin/worker_messages/")({
  component: WorkerMessagesListPage,
});

function WorkerMessagesListPage() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { conversations, loading, totalUnreadCount } =
    useUnifiedWorkerCommunications(userProfile?.uid);

  const handleConversationCreated = (workerId: string) => {
    navigate({
      to: "/dashboard/admin/worker_messages/$workerId",
      params: { workerId },
    });
  };

  if (!userProfile) {
    return null;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="shrink-0 border-b bg-white px-4 py-3 shadow-sm sm:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-8 w-8 text-green-600" />
            <div>
              <h1 className="text-2xl font-bold">Worker Messages</h1>
              <p className="text-sm text-gray-600">
                {totalUnreadCount > 0
                  ? `${totalUnreadCount} unread message${totalUnreadCount !== 1 ? "s" : ""}`
                  : "Chat with workers"}
              </p>
            </div>
          </div>
          <NewWorkerMessageDialog
            currentUserId={userProfile.uid}
            currentUserName={userProfile.name}
            onConversationCreated={handleConversationCreated}
          />
        </div>
      </div>

      {/* Conversation Layout */}
      <div className="mx-auto flex w-full max-w-7xl flex-1 gap-4 overflow-hidden p-4 sm:p-6">
        {/* Conversation List */}
        <div className="w-full overflow-hidden rounded-lg border bg-white shadow-sm md:w-80 md:shrink-0">
          <WorkerConversationList
            conversations={conversations}
            loading={loading}
          />
        </div>

        {/* Empty State for Desktop */}
        <div className="hidden flex-1 items-center justify-center rounded-lg border bg-white shadow-sm md:flex">
          <div className="text-center text-gray-500">
            <MessageCircle className="mx-auto mb-4 h-16 w-16 text-gray-300" />
            <h3 className="mb-2 text-lg font-medium">
              Select a conversation
            </h3>
            <p className="text-sm">
              Choose a worker from the list or start a new conversation
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
